import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import FormContainer from "../../../components/FormContainer";
import Workorder from '../../../models/Workorder';
import WorkorderBrg from '../../../models/WorkorderBrg';
import WorkorderKaryawan from '../../../models/WorkorderKaryawan';
import { useStateContext } from '../../../reducers';
import DataForm from '../../../components/DataForm';
import { buttonTheme, getPathObject, pageToolbarDetailHeight, STATUS_BOOK_SERV_VHC_APV, STATUS_BOOK_SERV_VHC_APV_CUST, STATUS_BOOK_SERV_VHC_COMPLETED, STATUS_BOOK_SERV_VHC_IN_PROGRESS, STATUS_BOOK_SERV_VHC_NEW } from '../../../utils/Common';
import Loader from '../../../components/Loader';
import { SaveDataParamType } from '../../../utils/Types';
import { MenuCode, MenuGroupName, MenuTitle } from '../../../utils/Menu';
import {
    Add as AddIcon,
    SaveAs as SaveAsIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    HowToReg as HowToRegIcon,
    Done as DoneIcon,
} from '@mui/icons-material';
import FormModal from '../../../components/FormModal';
import { Box, Button, ButtonGroup, SvgIconOwnProps, Tooltip, Typography } from '@mui/material';
import { ThemeProvider } from '@emotion/react';
import GridViewEdit from '../../../components/GridViewEdit';
import Barang from '../../../models/Barang';
import { v4 as uuidv4, validate as uuidValidate } from "uuid";
import Satuan from '../../../models/Satuan';

const groupName: MenuGroupName = "Servis";
const subgroupName: string = "Transaksi";
const code: MenuCode = "SERVICE-WORKORDER";
const title: MenuTitle = "Workorder - Detail";
const endpoint = "workorder";

const gridIconStyle : SvgIconOwnProps = {
    fontSize: "small",
    sx: {marginRight: "0.1rem", paddingBottom: 0},
};

function FormDetail() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
    const [initialData, setInitialData] = useState<Workorder>(({} as Workorder));
    const [formData, setFormData] = useState<Workorder>(({} as Workorder));
    const [detailBrgToEdit, setDetailBrgToEdit] = useState<WorkorderBrg | null>(null);
    const [detailBrgToDelete, setDetailBrgToDelete] = useState<WorkorderBrg | null>(null);
    const [detailKaryawanToEdit, setDetailKaryawanToEdit] = useState<WorkorderKaryawan | null>(null);
    const [detailKaryawanToDelete, setDetailKaryawanToDelete] = useState<WorkorderKaryawan | null>(null);
    const [validationResult, setValidationResult] = useState<any[] | null>(null);
    const navigate = useNavigate();
    const brgRef = useRef();
    const satuanRef = useRef();
    const karyawanRef = useRef();
    const bookingServiceRef = useRef<string | null>(null);
    
    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);
    const parentUrl: string | null = useMemo(() => location?.state?.parentUrl ?? null, [location]);

	useEffect(() => {
		if (!location?.state?.ucode)
            return;
        
        setFormData(JSON.parse(JSON.stringify(location.state)) as Workorder);
        setInitialData(JSON.parse(JSON.stringify(location.state)) as Workorder);
        bookingServiceRef.current = location.state.ucode_booking_service;
	}, [location.state]);

    const onDetailBrgChanged = useCallback((detailData?: WorkorderBrg) => {
        if (!detailData)
            return;

        const { list_workorder_brg } = formData;
        const current_list_workorder_brg = list_workorder_brg ?? [];
        detailData.sub_total_rp = (detailData.qty ?? 0) * (detailData.harga_rp ?? 0);

        if (detailData.brg)
        {
            detailData.kode_brg = detailData.brg.kode;
            detailData.nama_brg = detailData.brg.nama;
            delete detailData.brg;
        }

        if (detailData.satuan)
        {
            detailData.kode_satuan = detailData.satuan.kode;
            detailData.nama_satuan = detailData.satuan.nama;
            delete detailData.satuan;
        }

        if (!detailData.ucode)
        {
            detailData.ucode_workorder = formData.ucode;
            detailData.ucode = uuidv4();
            current_list_workorder_brg.push(detailData);
        }
        else {
            const currentDetail = current_list_workorder_brg.filter(d => d.ucode === detailData.ucode)[0];
            const currentIndex = currentDetail ? current_list_workorder_brg.indexOf(currentDetail) : -1;
            current_list_workorder_brg?.splice(currentIndex, 1, detailData);
        }

        const newData = {
            ...formData,
            list_workorder_brg: current_list_workorder_brg,
        };

        setFormData(JSON.parse(JSON.stringify(newData)));
    }, [formData, apiCaller]);

    const onDetailKaryawanChanged = useCallback((detailData?: WorkorderKaryawan) => {
        if (!detailData)
            return;
        
        const { list_workorder_karyawan } = formData;
        const current_list_workorder_karyawan = list_workorder_karyawan ?? [];
        detailData.sub_total_rp = 0;

        if (detailData.karyawan) {
            detailData.kode_karyawan = detailData.karyawan.kode;
            detailData.nama_karyawan = detailData.karyawan.nama;
            delete detailData.karyawan;
        }

        if (!detailData.ucode)
        {
            detailData.ucode_workorder = formData.ucode;
            detailData.ucode = uuidv4();
            current_list_workorder_karyawan.push(detailData);
        }
        else {
            const currentDetail = current_list_workorder_karyawan.filter(d => d.ucode === detailData.ucode)[0];
            const currentIndex = currentDetail ? current_list_workorder_karyawan.indexOf(currentDetail) : -1;
            current_list_workorder_karyawan?.splice(currentIndex, 1, detailData);
        }

        const newData = {
            ...formData,
            list_workorder_karyawan: current_list_workorder_karyawan,
        };

        setFormData(JSON.parse(JSON.stringify(newData)));
    }, [formData, apiCaller]);

    const onValidate = useCallback((data: Workorder | undefined) => {
        return true;
    }, []);

    const onClose = useCallback(() => {
        const pathObject = getPathObject(groupName, subgroupName, "SERVICE-BOOKING-WO");
        navigate(parentUrl ?? `${pathObject?.mainPath}${pathObject?.formPath}`, {state: {ucode: bookingServiceRef.current, isViewMode}});
    }, [navigate, bookingServiceRef.current, parentUrl, isViewMode]);

    const onSave = useCallback((param: SaveDataParamType<Workorder> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);

        param.data.list_workorder_brg = param.data.list_workorder_brg?.map(brg => {
            return {
                ucode: brg.is_new ? null : brg.ucode,
                ucode_brg: brg.ucode_brg,
                ucode_satuan: brg.ucode_satuan,
                harga_rp: brg.harga_rp,
                qty: brg.qty,
                pajak_rp: brg.pajak_rp,
                ucode_workorder: brg.ucode_workorder,
            };
        }) ?? [];

        param.data.list_workorder_karyawan = param.data.list_workorder_karyawan?.map(kry => {
            return {
                ucode: kry.is_new ? null : kry.ucode,
                ucode_karyawan: kry.ucode_karyawan,
                ucode_workorder: kry.ucode_workorder,
            };
        }) ?? [];

        const apiCallerObj = param.data.ucode ? apiCaller.put(`${endpoint}`, JSON.stringify(param.data)) : apiCaller.post(`${endpoint}`, JSON.stringify(param.data));

        apiCallerObj
            .then(() => {
                setLoading(false);

                if (param.callback)
                    param.callback();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller]);
    
    const handleSetToApproveInternal = useCallback((dataToSend?: Workorder) => {
        if (!apiCaller || !dataToSend)
            return;

        setLoading(true);

        apiCaller.put(`${endpoint}/approve-internal?id=${dataToSend.ucode}`)
            .then(() => {
                const pathObject = getPathObject(groupName, subgroupName, "SERVICE-BOOKING-WO");
                setLoading(false);
                navigate(parentUrl ?? `${pathObject?.mainPath}${pathObject?.formPath}`, {state: {ucode: bookingServiceRef.current, isViewMode}});
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller, parentUrl]);
    
    const handleSetToApproveByCustomer = useCallback((dataToSend?: Workorder) => {
        if (!apiCaller || !dataToSend)
            return;

        setLoading(true);

        apiCaller.put(`${endpoint}/approve-workorder`, JSON.stringify(dataToSend))
            .then(() => {
                const pathObject = getPathObject(groupName, subgroupName, "SERVICE-BOOKING-WO");
                setLoading(false);
                navigate(parentUrl ?? `${pathObject?.mainPath}${pathObject?.formPath}`, {state: {ucode: bookingServiceRef.current, isViewMode}});
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller, parentUrl]);
    
    const handleSetToInProgress = useCallback((dataToSend?: Workorder) => {
        if (!apiCaller || !dataToSend)
            return;

        setLoading(true);

        apiCaller.put(`${endpoint}/change-status?id=${dataToSend.ucode}`)
            .then(() => {
                const pathObject = getPathObject(groupName, subgroupName, "SERVICE-BOOKING-WO");
                setLoading(false);
                navigate(parentUrl ?? `${pathObject?.mainPath}${pathObject?.formPath}`, {state: {ucode: bookingServiceRef.current, isViewMode}});
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller, parentUrl]);

    const handleAmendWorkorder = useCallback((dataToCompare?: Workorder) => {
        if (!apiCaller || !dataToCompare)
            return;

        if (!dataToCompare.list_workorder_karyawan?.length)
            return;

        setLoading(true);

        const dataToSend = {
            ucode: dataToCompare.ucode,
            list_ubah_workorder_brg: dataToCompare.list_workorder_brg?.filter((brg: WorkorderBrg) => initialData.list_workorder_brg?.filter((brg_c: WorkorderBrg) => brg_c.ucode === brg.ucode && (brg_c.qty !== brg.qty || brg_c.harga_rp !== brg.harga_rp))?.length) ?? [],
            list_tambah_workorder_brg: dataToCompare.list_workorder_brg?.filter((brg: WorkorderBrg) => brg.is_new) ?? [],
            list_workorder_karyawan: dataToCompare.list_workorder_karyawan ?? [],
        };

        apiCaller.put(`${endpoint}/maintain-workorder`, JSON.stringify(dataToSend))
            .then(() => {
                const pathObject = getPathObject(groupName, subgroupName, "SERVICE-BOOKING-WO");
                setLoading(false);
                navigate(parentUrl ?? `${pathObject?.mainPath}${pathObject?.formPath}`, {state: {ucode: bookingServiceRef.current, isViewMode}});
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller, initialData]);
    
    const handleSetToCompleted = useCallback((dataToCompare?: Workorder) => {
        if (!apiCaller || !dataToCompare)
            return;

        setLoading(true);

        const dataToSend = {
            list_workorder_completed: [
                {
                    ucode: dataToCompare.ucode
                },
            ],
        };

        apiCaller.put(`${endpoint}/status-completed`, JSON.stringify(dataToSend))
            .then(() => {
                const pathObject = getPathObject(groupName, subgroupName, "SERVICE-BOOKING-WO");
                setLoading(false);
                navigate(parentUrl ?? `${pathObject?.mainPath}${pathObject?.formPath}`, {state: {ucode: bookingServiceRef.current, isViewMode}});
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller, parentUrl]);

    return (
        <>
        {
            loading && <Loader />
        }
            <FormContainer
                groupName={groupName}
                subgroupName={subgroupName}
                code={code}
                title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
            >
                <DataForm<Workorder>
                    groupName={groupName}
                    subgroupName={subgroupName}
                    code={code}
                    title={title}
                    data={formData}
					isViewMode={isViewMode}
                    hideDefaultCommands={formData.kode_status_booking_service_kendaraan === STATUS_BOOK_SERV_VHC_IN_PROGRESS || formData.kode_status_booking_service_kendaraan === STATUS_BOOK_SERV_VHC_COMPLETED}
                    customFormCommands={[
                        {
                            title: "Amend",
                            action: (rowData?: Workorder) => handleAmendWorkorder(rowData),
                            icon: SaveAsIcon,
                            color: "primary",
                            disabled: formData.kode_status_booking_service_kendaraan !== STATUS_BOOK_SERV_VHC_IN_PROGRESS,
                        },
                        {
                            title: "Approve Internal",
                            action: (rowData?: Workorder) => handleSetToApproveInternal(rowData),
                            icon: HowToRegIcon,
                            color: "edit",
                            disabled: formData.kode_status_booking_service_kendaraan !== STATUS_BOOK_SERV_VHC_NEW,
                        },
                        {
                            title: "Approve by Customer",
                            action: (rowData?: Workorder) => handleSetToApproveByCustomer(rowData),
                            icon: HowToRegIcon,
                            color: "edit",
                            disabled: formData.kode_status_booking_service_kendaraan !== STATUS_BOOK_SERV_VHC_APV,
                        },
                        {
                            title: "Proses",
                            action: (rowData?: Workorder) => handleSetToInProgress(rowData),
                            icon: DoneIcon,
                            color: "edit",
                            disabled: formData.kode_status_booking_service_kendaraan !== STATUS_BOOK_SERV_VHC_APV_CUST,
                        },
                        {
                            title: "Set to Complete",
                            action: (rowData?: Workorder) => handleSetToCompleted(rowData),
                            icon: DoneIcon,
                            color: "success",
                            disabled: formData.kode_status_booking_service_kendaraan !== STATUS_BOOK_SERV_VHC_IN_PROGRESS,
                        },
                    ]}
                    fields={[
                        {
                            name: "no_booking_service",
                            title: "No. Booking Service",
                            inputType: "text",
                            dataType: (typeof formData.no_booking_service),
                            isReadonly: true,
                            isRequired: false,
                        },
                        {
                            name: "tanggal_booking_service",
                            title: "Tanggal Booking Service",
                            inputType: "date",
                            dataType: (typeof formData.tanggal_booking_service),
                            isReadonly: true,
                            isRequired: false,
                        },
                        {
                            name: "nama_customer",
                            title: "Customer",
                            inputType: "text",
                            dataType: (typeof formData.nama_customer),
                            isReadonly: true,
                            isRequired: false,
                        },
                        {
                            name: "nopol",
                            title: "No. Polisi",
                            inputType: "text",
                            dataType: (typeof formData.nopol),
                            isReadonly: true,
                            isRequired: false,
                        },
                        {
                            name: "jenis_kendaraan",
                            title: "Jenis Kendaraan",
                            inputType: "text",
                            dataType: (typeof formData.jenis_kendaraan),
                            isReadonly: true,
                            isRequired: false,
                        },
                        {
                            name: "nama_status_booking_service_kendaraan",
                            title: "Status",
                            inputType: "text",
                            dataType: (typeof formData.nama_status_booking_service_kendaraan),
                            isReadonly: true,
                            isRequired: false,
                        },
                        {
                            name: "keterangan_operator",
                            title: "Keterangan",
                            inputType: "textarea",
                            dataType: (typeof formData.keterangan_operator),
                            isReadonly: true,
                            isRequired: false,
                            rows: 3,
                        },
                    ]}
                    onValidate={onValidate}
                    onClose={() => onClose()}
                    onSave={(data) => onSave(data)}
                />
                <Box
                    sx={{
                        marginTop: "1.5rem",
                        marginBottom: "0.5rem",
                        width: "100%",
                        height: `${pageToolbarDetailHeight}px`,
                        paddingTop: "0.5rem",
                        paddingBottom: "0.5rem",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "start",
                        alignItems: "start",
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            textAlign: "start",
                            borderBottom: "2px solid #D9E7E5",
                            color: "#185E91",
                            marginBottom: "1rem",
                        }}
                    >
                        <Typography variant="button" sx={{fontSize: "1rem"}}>Detail Sparepart / Jasa</Typography>
                    </Box>
                    <Box
                        sx={{
                            width: "100%",
                        }}
                    >
                        <ThemeProvider theme={buttonTheme}>
                            <ButtonGroup>
                                <Tooltip
                                    title="Tambah Detail Barang"
                                    arrow
                                >
                                    <Button disabled={isViewMode} variant="contained" color="primary" size="small" onClick={() => setDetailBrgToEdit({ is_new: true })}>
                                        <AddIcon {...gridIconStyle} />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </ThemeProvider>
                    </Box>
                </Box>
                <GridViewEdit<WorkorderBrg>
                    id_field="ucode"
                    columns={[
                        {
                            columnName: "action",
                            columnTitle: "",
                            columnType: "action",
                            actions: [
                                {
                                    title: "Ubah",
                                    color: "primary",
                                    action: (rowData: WorkorderBrg) => setDetailBrgToEdit({ ...rowData, brg: rowData.kode_brg ? {ucode: rowData.ucode_brg, kode: rowData.kode_brg, nama: rowData.nama_brg} : undefined, satuan: rowData.kode_satuan ? {ucode: rowData.ucode_satuan, kode: rowData.kode_satuan, nama: rowData.nama_satuan, aktif: true} : undefined }),
                                    icon: EditIcon,
                                    variant: "outlined",
                                    disabled: (rowData: WorkorderBrg) => isViewMode || rowData?.cancelled,
                                },
                                {
                                    title: "Hapus",
                                    color: "delete",
                                    action: (rowData: WorkorderBrg) => {
                                        const deletedDetailIndex = formData.list_workorder_brg?.findIndex(d => d.ucode === rowData.ucode) ?? -1;

                                        if (deletedDetailIndex < 0)
                                            return;

                                        formData.list_workorder_brg?.splice(deletedDetailIndex, 1);
                                        setFormData(JSON.parse(JSON.stringify(formData)));
                                    },
                                    icon: DeleteIcon,
                                    variant: "outlined",
                                    disabled: (rowData: WorkorderBrg) => isViewMode || rowData?.cancelled,
                                }
                            ],
                            width: "10%",
                        },
                        {
                            columnName: "nama_brg",
                            columnTitle: "Barang",
                            columnType: "data",
                            titleColumn: true,
                        },
                        {
                            columnName: "kode_brg",
                            columnTitle: "Kode Barang",
                            columnType: "data",
                        },
                        {
                            columnName: "nama_satuan",
                            columnTitle: "Satuan",
                            columnType: "data",
                        },
                        {
                            columnName: "qty",
                            columnTitle: "Jumlah",
                            fieldType: "integer",
                            columnType: "data",
                        },
                        {
                            columnName: "harga_rp",
                            columnTitle: "Harga Satuan (Rp)",
                            fieldType: "float",
                            columnType: "data",
                        },
                        {
                            columnName: "sub_total_rp",
                            columnTitle: "Sub Total (Rp)",
                            fieldType: "float",
                            columnType: "data",
                        },
                    ]}
                    data={formData.list_workorder_brg?.filter(brg => !brg.cancelled) ?? []}
                    containerStyle={{
                        marginTop: "0.2rem",
                    }}
                />
                <Box
                    sx={{
                        marginTop: "1.5rem",
                        marginBottom: "0.5rem",
                        width: "100%",
                        height: `${pageToolbarDetailHeight}px`,
                        paddingTop: "0.5rem",
                        paddingBottom: "0.5rem",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "start",
                        alignItems: "start",
                    }}
                >
                    <Box
                        sx={{
                            width: "100%",
                            textAlign: "start",
                            borderBottom: "2px solid #D9E7E5",
                            color: "#185E91",
                            marginBottom: "1rem",
                        }}
                    >
                        <Typography variant="button" sx={{fontSize: "1rem"}}>Detail Karyawan</Typography>
                    </Box>
                    <Box
                        sx={{
                            width: "100%",
                        }}
                    >
                        <ThemeProvider theme={buttonTheme}>
                            <ButtonGroup>
                                <Tooltip
                                    title="Tambah Detail Barang"
                                    arrow
                                >
                                    <Button disabled={isViewMode} variant="contained" color="primary" size="small" onClick={() => setDetailKaryawanToEdit({ is_new: true })}>
                                        <AddIcon {...gridIconStyle} />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </ThemeProvider>
                    </Box>
                </Box>
                <GridViewEdit<WorkorderKaryawan>
                    id_field="ucode"
                    columns={[
                        {
                            columnName: "action",
                            columnTitle: "",
                            columnType: "action",
                            actions: [
                                {
                                    title: "Ubah",
                                    color: "primary",
                                    action: (rowData: WorkorderKaryawan) => setDetailKaryawanToEdit({ ...rowData, karyawan: rowData.kode_karyawan ? {ucode: rowData.ucode_karyawan, kode: rowData.kode_karyawan, nama: rowData.nama_karyawan} : undefined }),
                                    icon: EditIcon,
                                    variant: "outlined",
                                    disabled: () => isViewMode,
                                },
                                {
                                    title: "Hapus",
                                    color: "delete",
                                    action: (rowData: WorkorderKaryawan) => {
                                        const deletedDetailIndex = formData.list_workorder_karyawan?.findIndex(d => d.ucode === rowData.ucode) ?? -1;

                                        if (deletedDetailIndex < 0)
                                            return;

                                        formData.list_workorder_karyawan?.splice(deletedDetailIndex, 1);
                                        setFormData(JSON.parse(JSON.stringify(formData)));
                                    },
                                    icon: DeleteIcon,
                                    variant: "outlined",
                                    disabled: () => isViewMode,
                                }
                            ],
                            width: "10%",
                        },
                        {
                            columnName: "nama_karyawan",
                            columnTitle: "Barang",
                            columnType: "data",
                            titleColumn: true,
                        },
                        {
                            columnName: "kode_karyawan",
                            columnTitle: "Kode Karyawan",
                            columnType: "data",
                        },
                    ]}
                    data={formData.list_workorder_karyawan}
                    containerStyle={{
                        marginTop: "0.2rem",
                    }}
                />
            </FormContainer>
        {
            detailBrgToEdit ?
            <FormModal<WorkorderBrg>
                apiCaller={apiCaller}
                data={detailBrgToEdit}
                title={`${detailBrgToEdit.is_new ? "Tambah" : "Ubah"} Detail Sparepart / Jasa`}
                fields={[
                    {
                        name: "brg",
                        keyField: "ucode",
                        labelField: "nama",
                        editKeyField: "ucode_brg",
                        title: "Sparepart",
                        inputType: "combobox",
                        dataType: (typeof Barang),
                        isReadonly: false,
                        isRequired: true,
                        searchEndpoint: "brg/nama",
                        searchEndpointFilterParamName: "nama",
                        ref: brgRef,
                        gridSizeSm: 12,
                        gridSizeMd: 12,
                        gridSizeLg: 12,
                        gridSizeXl: 6,
                    },
                    {
                        name: "satuan",
                        keyField: "ucode",
                        labelField: "nama",
                        editKeyField: "ucode_satuan",
                        title: "Satuan",
                        inputType: "combobox",
                        dataType: (typeof Satuan),
                        isReadonly: false,
                        isRequired: true,
                        searchEndpoint: `satuan/brg`,
                        searchEndpointPathParamName: ["ucode_brg"],
                        searchEndpointFilterParamName: "nama",
                        ref: satuanRef,
                        gridSizeSm: 12,
                        gridSizeMd: 12,
                        gridSizeLg: 12,
                        gridSizeXl: 6,
                    },
                    {
                        name: "qty",
                        title: "Jumlah",
                        inputType: "integer",
                        dataType: (typeof detailBrgToEdit.qty),
                        isReadonly: false,
                        isRequired: true,
                        gridSizeSm: 12,
                        gridSizeMd: 12,
                        gridSizeLg: 12,
                        gridSizeXl: 6,
                    },
                    {
                        name: "harga_rp",
                        title: "Harga (Rp)",
                        inputType: "float",
                        dataType: (typeof detailBrgToEdit.harga_rp),
                        isReadonly: false,
                        isRequired: true,
                        gridSizeSm: 12,
                        gridSizeMd: 12,
                        gridSizeLg: 12,
                        gridSizeXl: 6,
                    },
                ]}
                onClose={() => setDetailBrgToEdit(null)}
                onSave={(detailData?: WorkorderBrg) => {
                    onDetailBrgChanged(detailData);
                    setDetailBrgToEdit(null);
                }}
            />
            :
            null
        }
        {
            detailKaryawanToEdit ?
            <FormModal<WorkorderKaryawan>
                apiCaller={apiCaller}
                data={detailKaryawanToEdit}
                title={detailKaryawanToEdit.is_new ? "Tambah Detail Karyawan" : "Ubah Detail Karyawan"}
                fields={[
                    {
                        name: "karyawan",
                        keyField: "ucode",
                        labelField: "nama",
                        editKeyField: "ucode_karyawan",
                        title: "Karyawan",
                        inputType: "combobox",
                        dataType: (typeof Barang),
                        isReadonly: false,
                        isRequired: true,
                        searchEndpoint: "karyawan/nama",
                        searchEndpointFilterParamName: "nama",
                        ref: karyawanRef,
                        gridSizeSm: 12,
                        gridSizeMd: 12,
                        gridSizeLg: 12,
                        gridSizeXl: 6,
                    },
                ]}
                onClose={() => setDetailKaryawanToEdit(null)}
                onSave={(detailData?: WorkorderKaryawan) => {
                    onDetailKaryawanChanged(detailData);
                    setDetailKaryawanToEdit(null);
                }}
            />
            :
            null
        }
        </>
    );
};

export default FormDetail;
